import { ArrowPathIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConfirmationModal } from '../ConfirmationModal';
import { useEditor } from '../context/EditorContext/EditorContext';
import { useNotifications } from '../context/NotificationContext/NotificationContext';
import { API } from '../utils/api';

const POLL_INTERVAL_MS = 8_000;
const MAX_WAIT_MS = 35 * 60 * 1_000;

export function RegenerateSite() {
    const editor = useEditor();
    const notifications = useNotifications();
    const { company_id, domain } = editor.global.data;
    const [instructions, setInstructions] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [starting, setStarting] = useState(false);
    const pollTimer = useRef(null);

    useEffect(
        () => () => {
            if (pollTimer.current) window.clearTimeout(pollTimer.current);
        },
        []
    );

    const fail = (error) => {
        if (pollTimer.current) window.clearTimeout(pollTimer.current);
        setRegenerating(false);
        setStarting(false);
        notifications.push({
            title: 'Site regeneration failed',
            message:
                error?.message ||
                'The current site was left unchanged. Please try again.',
            variant: 'error',
            error,
        });
    };

    const pollUntilReady = (baselineSha, startedAt, failedChecks = 0) => {
        pollTimer.current = window.setTimeout(async () => {
            if (Date.now() - startedAt > MAX_WAIT_MS) {
                fail(
                    new Error(
                        'Regeneration is taking longer than expected. Reload later to check the result.'
                    )
                );
                return;
            }

            try {
                const status = await API.get(
                    `${company_id}/regeneration/status`,
                    baselineSha ? { params: { baselineSha } } : undefined
                );
                if (status?.status === 'ready') {
                    window.location.reload();
                    return;
                }
                pollUntilReady(baselineSha, startedAt, 0);
            } catch (error) {
                if (failedChecks >= 4) {
                    fail(error);
                    return;
                }
                pollUntilReady(baselineSha, startedAt, failedChecks + 1);
            }
        }, POLL_INTERVAL_MS);
    };

    const regenerateSite = async () => {
        setStarting(true);
        setConfirming(false);
        setRegenerating(true);
        try {
            const result = await API.post(`${company_id}/regeneration`, {
                instructions: instructions.trim(),
                domain: String(domain || ''),
            });
            setStarting(false);
            pollUntilReady(result?.baselineSha || '', Date.now());
        } catch (error) {
            fail(error);
        }
    };

    const overlay =
        regenerating && typeof document !== 'undefined'
            ? createPortal(
                  <div
                      className="fixed inset-0 z-[100000] flex items-center justify-center bg-white/45 px-6 backdrop-blur-md"
                      role="status"
                      aria-live="polite"
                      aria-label="Site regeneration in progress"
                  >
                      <div className="flex max-w-md flex-col items-center rounded-2xl border border-white/70 bg-white/90 px-8 py-7 text-center shadow-2xl">
                          <ArrowPathIcon className="h-11 w-11 animate-spin text-primary-700" />
                          <h2 className="mt-4 text-lg font-semibold text-neutral-900">
                              Your site is regenerating
                          </h2>
                          <p className="mt-2 text-sm leading-6 text-neutral-600">
                              We’re rebuilding every page from your
                              instructions. This page will reload automatically
                              when the new site is ready.
                          </p>
                      </div>
                  </div>,
                  document.body
              )
            : null;

    return (
        <>
            <div className="rounded-xl border border-red-200 bg-white p-4 shadow-sm">
                <p className="text-[11px] leading-4 text-neutral-500">
                    Describe the components, colors, styling, and copy you want.
                    You can include a website URL and ask for a similar
                    structure or the same copy.
                </p>
                <div className="mt-3 rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                    <textarea
                        rows={7}
                        maxLength={12000}
                        value={instructions}
                        onChange={(event) =>
                            setInstructions(event.target.value)
                        }
                        className="block w-full resize-y rounded-[10px] border-0 bg-white px-3 py-2 text-sm leading-6 text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
                        placeholder="Example: Use a full-background hero, a USP section, and a review stories slider. Use #2457D6 as the primary color and rounded buttons. Review https://example.com and use a similar structure and the same copy."
                        disabled={starting || regenerating}
                    />
                </div>
                <p className="mt-2 text-[11px] leading-4 text-red-600">
                    Regeneration replaces every page and all manual edits.
                    Existing backups remain available.
                </p>
                <button
                    type="button"
                    onClick={() => setConfirming(true)}
                    disabled={
                        !company_id ||
                        !instructions.trim() ||
                        starting ||
                        regenerating
                    }
                    className="mt-3 inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <SparklesIcon className="h-4 w-4" />
                    Regenerate site
                </button>
            </div>

            <ConfirmationModal
                variant="danger"
                title="Regenerate the entire site?"
                message={
                    <p className="text-sm text-gray-500">
                        Every page, component selection, generated copy, design
                        setting, and manual edit will be replaced. You can
                        restore an earlier deployment from Backup.
                    </p>
                }
                prompt="Regenerate"
                open={confirming}
                onClose={() => !starting && setConfirming(false)}
                onConfirm={regenerateSite}
                loading={starting}
            />
            {overlay}
        </>
    );
}
